// Geometry helpers for PDF comments. All coordinates are page fractions (0..1).
export const BOX_TYPES = ["rect", "ellipse", "cloud", "comment", "text", "image"];
export const FILL_TYPES = ["rect", "ellipse", "cloud", "comment", "callout", "note", "text"];
export const TEXT_TYPES = ["rect", "ellipse", "cloud", "comment", "callout", "text"];
export const BOXED_TEXT = ["comment", "callout", "text"];
export const FONT_STACKS = { sans: "Arial, Helvetica, sans-serif", serif: "'Times New Roman', Times, serif", mono: "'Courier New', Courier, monospace" };
export const MARKUP_TYPES = ["highlight", "underline", "strike"];
export const LINE_TYPES = ["line", "arrow"];
export const STICKY_TOOLS = ["draw", ...MARKUP_TYPES];

export const defaultStyle = (type) => ({
    color: type === "highlight" ? "#facc15" : type === "note" || type === "text" || type === "image" ? "#1f2937" : type === "comment" || type === "callout" ? "#2563eb" : "#ef4444",
    width: type === "image" ? 0 : 2,
    fill: type === "note" ? "#f59e0b" : type === "comment" || type === "callout" ? "#ffffff" : null,
    fillOpacity: type === "note" ? 1 : type === "comment" || type === "callout" ? 0.9 : 0,
    strokeOpacity: type === "text" ? 0 : 1,
    textColor: "#111827",
    fontSize: 12, fontFamily: "sans", bold: false, italic: false, underline: false,
    align: BOXED_TEXT.includes(type) ? "left" : "center",
});
export const fullStyle = (c) => {
    const s = { ...defaultStyle(c.type), ...(c.style || {}) };
    if (c.type === "note" && !(c.style || {}).fill && (c.style || {}).color) s.fill = c.style.color;
    return s;
};

export const boxFrom = (a, b) => ({ x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(a.x - b.x), h: Math.abs(a.y - b.y) });

export function bboxOf(c, noteW = 0.025, noteH = 0.025) {
    const g = c.geom || {};
    if (c.type === "callout") return boxFrom({ x: Math.min(g.x, g.ax), y: Math.min(g.y, g.ay) }, { x: Math.max(g.x + g.w, g.ax), y: Math.max(g.y + g.h, g.ay) });
    if (BOX_TYPES.includes(c.type)) return { x: g.x, y: g.y, w: g.w, h: g.h };
    if (c.type === "note") return { x: g.x, y: g.y, w: noteW, h: noteH };
    if (LINE_TYPES.includes(c.type)) return boxFrom({ x: g.x1, y: g.y1 }, { x: g.x2, y: g.y2 });
    const pts = c.type === "draw" ? g.points || [] : (g.rects || []).flatMap((r) => [[r.x, r.y], [r.x + r.w, r.y + r.h]]);
    if (!pts.length) return { x: 0, y: 0, w: 0, h: 0 };
    const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1]);
    const x = Math.min(...xs), y = Math.min(...ys);
    return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

export function moveGeom(type, g, dx, dy) {
    if (type === "image") return { ...g, x: g.x + dx, y: g.y + dy };
    if (type === "callout") return { ...g, x: g.x + dx, y: g.y + dy, ax: g.ax + dx, ay: g.ay + dy };
    if (BOX_TYPES.includes(type) || type === "note") return { ...g, x: g.x + dx, y: g.y + dy };
    if (LINE_TYPES.includes(type)) return { x1: g.x1 + dx, y1: g.y1 + dy, x2: g.x2 + dx, y2: g.y2 + dy };
    if (type === "draw") return { points: g.points.map(([x, y]) => [x + dx, y + dy]) };
    return { rects: g.rects.map((r) => ({ ...r, x: r.x + dx, y: r.y + dy })) };
}

const resizeBox = (b, handle, dx, dy) => {
    let { x, y, w, h } = b;
    if (handle.includes("w")) { x += dx; w -= dx; }
    if (handle.includes("e")) w += dx;
    if (handle.includes("n")) { y += dy; h -= dy; }
    if (handle.includes("s")) h += dy;
    if (w < 0) { x += w; w = -w; }
    if (h < 0) { y += h; h = -h; }
    return { x, y, w: Math.max(w, 0.004), h: Math.max(h, 0.004) };
};

export function resizeGeom(type, g, handle, dx, dy) {
    if (type === "image") return { ...g, ...resizeBox(g, handle, dx, dy) };
    if (type === "callout") return handle === "a" ? { ...g, ax: g.ax + dx, ay: g.ay + dy } : { ...g, ...resizeBox(g, handle, dx, dy) };
    if (BOX_TYPES.includes(type)) return resizeBox(g, handle, dx, dy);
    if (LINE_TYPES.includes(type)) {
        return handle === "p1" ? { ...g, x1: g.x1 + dx, y1: g.y1 + dy } : { ...g, x2: g.x2 + dx, y2: g.y2 + dy };
    }
    if (type === "draw") {
        const b = bboxOf({ type, geom: g });
        const nb = resizeBox(b, handle, dx, dy);
        const sx = b.w ? nb.w / b.w : 1, sy = b.h ? nb.h / b.h : 1;
        return { points: g.points.map(([x, y]) => [nb.x + (x - b.x) * sx, nb.y + (y - b.y) * sy]) };
    }
    return g;
}

export const canResize = (type) => BOX_TYPES.includes(type) || LINE_TYPES.includes(type) || type === "draw" || type === "callout";

// Point on the box edge closest to the anchor (callout leader start).
export function calloutStart(g) {
    const cx = Math.min(Math.max(g.ax, g.x), g.x + g.w), cy = Math.min(Math.max(g.ay, g.y), g.y + g.h);
    if (cx > g.x && cx < g.x + g.w && cy > g.y && cy < g.y + g.h) return { x: g.x, y: g.y + g.h / 2 };
    return { x: cx, y: cy };
}

// Scalloped "cloud" outline around a rectangle (pixel coords).
export function cloudPath(x, y, w, h, r) {
    const seg = (len) => Math.max(1, Math.round(len / (r * 2)));
    const nx = seg(w), ny = seg(h);
    const sx = w / nx, sy = h / ny;
    const rx = Math.max(sx, sy) / 2 + 1;
    let d = `M ${x} ${y}`;
    for (let i = 1; i <= nx; i++) d += ` A ${rx} ${rx} 0 0 1 ${x + i * sx} ${y}`;
    for (let i = 1; i <= ny; i++) d += ` A ${rx} ${rx} 0 0 1 ${x + w} ${y + i * sy}`;
    for (let i = 1; i <= nx; i++) d += ` A ${rx} ${rx} 0 0 1 ${x + w - i * sx} ${y + h}`;
    for (let i = 1; i <= ny; i++) d += ` A ${rx} ${rx} 0 0 1 ${x} ${y + h - i * sy}`;
    return `${d} Z`;
}

export function arrowHead(x1, y1, x2, y2, size) {
    const a = Math.atan2(y2 - y1, x2 - x1);
    const p = (ang) => [x2 - size * Math.cos(ang), y2 - size * Math.sin(ang)];
    const [l, r] = [p(a - Math.PI / 7), p(a + Math.PI / 7)];
    return [[x2, y2], l, r];
}

// Rects (fractions) for text between two points, Acrobat-like flowing selection. null if no text.
export function textRectsBetween(items, a, b) {
    if (!items || !items.length) return null;
    const [start, end] = a.y <= b.y ? [a, b] : [b, a];
    const out = [];
    for (const it of items) {
        const onStart = start.y >= it.y && start.y <= it.y + it.h;
        const onEnd = end.y >= it.y && end.y <= it.y + it.h;
        const cy = it.y + it.h / 2;
        let lo = -Infinity, hi = Infinity;
        if (onStart && onEnd) { lo = Math.min(a.x, b.x); hi = Math.max(a.x, b.x); }
        else if (onStart) lo = start.x;
        else if (onEnd) hi = end.x;
        else if (!(cy > start.y && cy < end.y)) continue;
        const cw = it.w / Math.max(1, it.n);
        const x0 = Math.max(it.x, it.x + Math.floor((lo - it.x) / cw) * cw);
        const x1 = Math.min(it.x + it.w, it.x + Math.ceil((hi - it.x) / cw) * cw);
        if (x1 - x0 > 0.0005) out.push({ x: x0, y: it.y, w: x1 - x0, h: it.h });
    }
    return out.length ? out : null;
}
